import React from 'react'
import cn from 'classnames'
import styled from '@emotion/styled'

import { fileClass } from 'util/mime'
import { humanFileSize } from 'util/strings'
import { Flex, Icon, MindfulText } from 'shared/ui'
import styles from './styles.css'

const fixDownloadUrl = (url, subdomain) => {
  return url && url.replace('files.', `${subdomain}.`)
}

class Attachment extends React.PureComponent {
  onDelete = () => {
    const {
      afterDelete,
      doDeleteFile,
      attachment,
      forNote,
      draftId,
    } = this.props
    doDeleteFile(attachment, forNote, draftId)
    if (afterDelete) afterDelete(attachment, forNote, draftId)
  }

  render() {
    const { isImage, ...props } = this.props
    if (isImage) return <ImageAttachment {...props} onDelete={this.onDelete} />
    return <FileAttachment {...props} onDelete={this.onDelete} />
  }
}

const FileAttachment = ({
  attachment,
  className,
  onDelete,
  error,
  forDraft,
  forNote,
  isSingle,
  isUploading,
  subdomain,
}) => {
  return (
    <div
      className={cn(
        className,
        'file',
        styles.attachment,
        styles['attachment-fileAttachment'],
        {
          [styles['attachment-uploading']]: isUploading,
          [styles['attachment-forNote']]: forNote,
        }
      )}
    >
      <Flex
        className={cn(
          'nameWithSize overflow-hidden',
          styles.attachment_nameWithSize
        )}
      >
        {forDraft && (
          <Icon
            name="close"
            size="small"
            className="grui mr-3"
            onClick={onDelete}
          />
        )}
        {!isUploading &&
          !error && (
            <React.Fragment>
              <MindfulText
                as="a"
                className="hidden-in-print"
                href={fixDownloadUrl(attachment.download_url, subdomain)}
                target="_blank"
                download={fileClass(attachment.file_type) !== 'text'}
                truncate
              >
                {attachment.file_name}
              </MindfulText>
              <MindfulText className="visible-only-in-print" truncate>
                {attachment.file_name}
              </MindfulText>
            </React.Fragment>
          )}
        {!isUploading &&
          error && <MindfulText truncate>{attachment.file_name}</MindfulText>}
        {!isUploading &&
          forDraft &&
          error && (
            <Icon
              className="grui ml-3"
              size="xsmall"
              color="colot.primary.negative"
              name="error"
            />
          )}
        {isSingle &&
          !forNote && (
            <div
              className={cn('Icon Icon-attachment', styles.attachmentIcon)}
            />
          )}
        <FileSize isUploading={isUploading} attachment={attachment} />
        {isUploading && <ProgressBar attachment={attachment} />}
      </Flex>
    </div>
  )
}

const ImageAttachment = ({
  attachment,
  className,
  onDelete,
  error,
  forDraft,
  forNote,
  isUploading,
  onClick,
}) => {
  return (
    <div
      className={cn(
        className,
        styles.attachment,
        styles['attachment-imageAttachment'],
        {
          [styles['attachment-uploading']]: isUploading,
          [styles['attachment-forNote']]: forNote,
        }
      )}
    >
      {isUploading && (
        <div
          className={cn(
            'imagePreview',
            styles.attachment_imagePreview,
            styles['attachment_imagePreview-upload']
          )}
        >
          <FileTypeIcon attachment={attachment} />
        </div>
      )}
      {!isUploading &&
        !error && (
          <img
            alt={attachment.file_name}
            className={cn(
              'imagePreview',
              'hidden-in-print',
              styles.attachment_imagePreview
            )}
            onClick={onClick}
            src={attachment.url}
          />
        )}

      {isUploading && (
        <div className={cn('nameWithSize', styles.attachment_nameWithSize)}>
          <ProgressBar attachment={attachment} />
          <FileSize isUploading={isUploading} attachment={attachment} />
        </div>
      )}
      {!isUploading && (
        <Flex
          className={cn(
            'nameWithSize overflow-hidden',
            styles.attachment_nameWithSize
          )}
        >
          {forDraft && (
            <Icon
              name="close"
              className="grui mr-3"
              size="small"
              onClick={onDelete}
            />
          )}
          <MindfulText truncate>{attachment.file_name}</MindfulText>
          {!error && (
            <FileSize isUploading={isUploading} attachment={attachment} />
          )}
          {error &&
            forDraft && (
              <Icon
                className="grui ml-3"
                size="xsmall"
                color="color.primary.negative"
                name="error"
              />
            )}
        </Flex>
      )}
    </div>
  )
}

const FileTypeIcon = ({ attachment }) => {
  return (
    <div
      className={cn(
        styles.attachmentTypeIcon,
        `${styles.attachmentTypeIcon}-${fileClass(attachment.file_type)}`
      )}
    />
  )
}

const FileSize = ({ isUploading, attachment }) => {
  return (
    <div className={cn('size', styles.attachment_size)}>
      {isUploading
        ? `${attachment.progress || 0}%`
        : humanFileSize(attachment.file_size)}
    </div>
  )
}

const ProgressBar = ({ attachment }) => {
  return (
    <div className={styles.progressBar}>
      <div className={styles.progressBar_bg} />
      <div
        className={styles.progressBar_bar}
        style={{ transform: `scaleX(${attachment.progress / 100})` }}
      />
    </div>
  )
}

export default styled(Attachment)`
  color: ${props => props.theme.color.monochrome.medium};

  .name {
    background: ${props => props.theme.color.primary.brand};
  }

  .imagePreview {
    border: 1px solid ${props => props.theme.color.monochrome.light};
    border-radius: 2px;
    height: 80px;
  }

  .size {
    font-size: ${props => props.theme.fontSize.tiny};
  }

  .nameWithSize {
    font-weight: ${props => props.theme.fontWeight.medium};
  }
`
